/* eslint-disable react-hooks/exhaustive-deps */
import {
  Accordion, Button, Flex, LoadingOverlay, Text,
} from "@mantine/core";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { PencilIcon, SortAsc } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useWorkspaceObjectSchema } from "../../../../../hooks/QueryHooks";
import { LegacyColumn } from "../../../../../shared/table/legacyTanStackAdapter";
import TanStackTable from "../../../../../shared/table/TanStackTable";
import useObjectSearchTable from "../../../../../shared/table/useObjectSearchTable";
import MultiSelectDropdown from "../../../../../shared/ui/dropdown/MultiSelectDropdown";
import SortDropdown from "../../../../../shared/ui/dropdown/SortDropdown";
import useSignalStore from "../../../../../store/signal/signalStore";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import parseTableData from "../../../../../utils/parseTable";
import { getEntityLabel, getDefaultSortField } from "../../../../../shared/filter/utils/entityDisplayUtils";

const SignalPreviewTable = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const {
    signal,
    setSelectedFields,
    setSort: setSignalSort,
  } = useSignalStore((state) => ({
    signal: state.signal,
    setSort: state.setSort,
    setSelectedFields: state.setSelectedFields,
  }));

  const { data: workspaceSchema, isSuccess } = useWorkspaceObjectSchema({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  }) as any;

  const entitySlug = signal.entity_relation || "accounts";
  const traits = useMemo(() => (
    workspaceSchema?.fieldsByObjectSlug?.[entitySlug] || { list: [], obj: {} }
  ), [workspaceSchema?.fieldsByObjectSlug, entitySlug]);

  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const filteredTraits = useMemo(
    () => traits?.list?.filter((item: any) => !item.exclude_display) || [],
    [traits?.list],
  );

  const nameField = `${entitySlug}.name`;
  const lastSeenField = getDefaultSortField(entitySlug);

  const selectedWithRequiredColumns = useMemo(() => {
    const fields = [...(signal.selected_fields || [])];
    if (!fields.includes(nameField)) fields.unshift(nameField);
    if (!fields.includes(lastSeenField)) {
      fields.splice(fields.includes(nameField) ? 1 : 0, 0, lastSeenField);
    }
    return fields;
  }, [signal.selected_fields, nameField, lastSeenField]);

  const legacyColumns = useMemo<LegacyColumn[]>(() => {
    if (!traits?.obj) return [];
    return selectedWithRequiredColumns.reduce((columns: LegacyColumn[], key: string) => {
      const definition = traits.obj[key];
      if (!definition || definition.exclude_display) return columns;

      columns.push({
        key,
        title: definition.display_name,
        dataType: definition.data_type,
        isSortable: true,
        attributeType: definition.attribute_type,
        displayFormat: definition.display_format || null,
        generationType: definition.generation_type || null,
      });
      return columns;
    }, []);
  }, [selectedWithRequiredColumns, traits?.obj]);

  const initialSortField = signal.sort?.field || lastSeenField;
  const initialSortOrder = signal.sort?.order || "desc";

  const {
    rows,
    total,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setPageIndex,
    setSort: setTableSort,
    isLoading,
    isFetching,
  } = useObjectSearchTable({
    workspaceId: workspace?.id,
    entityType: entitySlug,
    enabled: !!workspace?.id && !!traits?.obj,
    initialPageSize: 5,
    initialSort: {
      field: initialSortField,
      order: initialSortOrder,
    },
    queryOptions: {
      filter: signal.filters,
      selectedFields: selectedWithRequiredColumns,
      fallbackSortField: lastSeenField,
    },
    parseRows: (rawRows) => parseTableData({
      data: (rawRows || []).map((record: any) => {
        const row: any = {};
        const prefix = entitySlug;
        const fields = record?.fields || {};
        Object.entries(fields).forEach(([key, value]) => {
          row[`${prefix}.${key}`] = value;
        });
        if (!row[`${prefix}.external_id`]) {
          row[`${prefix}.external_id`] = record?.id || fields?.external_id;
        }
        return row;
      }),
      memberData: [],
    }),
    resetPageDeps: [
      signal.entity_relation,
      JSON.stringify(signal.filters || {}),
      JSON.stringify(selectedWithRequiredColumns || []),
    ],
  });

  useEffect(() => {
    if (!signal.sort?.field) return;
    setTableSort({
      field: signal.sort.field,
      order: signal.sort.order,
    });
  }, [signal.sort?.field, signal.sort?.order, lastSeenField, setTableSort]);

  const onTableSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    const nextSort = (nextSorting || []).slice(0, 1)?.[0];
    if (!nextSort?.id) return;

    const nextField = nextSort.id;
    const nextOrder = nextSort.desc ? "desc" : "asc";

    setSignalSort({
      field: nextField,
      order: nextOrder,
      type: "property",
    });
    setTableSort({
      field: nextField,
      order: nextOrder,
    });
  }, [sorting, setSignalSort, setTableSort]);

  const onSelectColumn = (item: any) => {
    if (!item) return;

    if ((item.name === nameField || item.name === lastSeenField) && selectedWithRequiredColumns.includes(item.name)) {
      return;
    }

    const currentFields = [...(signal.selected_fields || [])];
    const newColumns = currentFields.includes(item.name)
      ? currentFields.filter((columnName) => columnName !== item.name)
      : [...currentFields, item.name];
    setSelectedFields(newColumns);
  };

  return (
    <>
      <Flex gap={8} align="center" mt={16} wrap="wrap">
        <MultiSelectDropdown
          selected={selectedWithRequiredColumns}
          setSelected={onSelectColumn}
          list={filteredTraits}
          metricsList={[]}
          position="bottom-end"
          isOpen={showColumnDropdown}
          setIsOpen={setShowColumnDropdown}
        >
          <Button size="xs" className="px-7 relative" leftSection={<PencilIcon size={14} />} variant="white">Edit Columns</Button>
        </MultiSelectDropdown>
        <SortDropdown
          list={filteredTraits}
          selectedColumns={selectedWithRequiredColumns}
          selectSort={{
            value: signal.sort?.field || "",
            type: signal.sort?.field ? (traits?.obj?.[signal.sort.field]?.data_type || "") : "",
            entityType: signal.sort?.field ? (traits?.obj?.[signal.sort.field]?.attribute_type || "") : "",
          }}
          selectSortDir={{ order: signal.sort?.order || "desc", type: "property" }}
          isOpen={showSortDropdown}
          setIsOpen={setShowSortDropdown}
          onDone={(field: any, dir: any) => {
            const nextSort = { field: field.value, order: dir.order, type: "property" };
            setSignalSort(nextSort);
            setTableSort({ field: nextSort.field, order: nextSort.order });
          }}
        >
          <div>
            <Button size="xs" className="!px-10 relative" leftSection={<SortAsc size={14} />} variant="white">Sort Columns</Button>
          </div>
        </SortDropdown>
      </Flex>

      <Accordion variant="contained" className="mt-6 bg-gray-50">
        <Accordion.Item value="preview">
          <Accordion.Control>
            <Flex gap={8} align="center" wrap="wrap">
              {total > 0
                ? <Text size="xs" className="italic " c="gray.7">{total} {getEntityLabel(entitySlug, workspaceSchema, "plural")} currently meet these conditions</Text>
                : <Text size="xs" className="italic " c="gray.7">No data found for current filters</Text>}
            </Flex>
          </Accordion.Control>
          <Accordion.Panel>
            <LoadingOverlay visible={!isSuccess} />
            <TanStackTable
              legacyColumns={legacyColumns}
              data={rows}
              totalData={total}
              pageIndex={pageIndex}
              pageSize={pageSize}
              pageCount={pageCount}
              onPageIndexChange={setPageIndex}
              sorting={sorting}
              onSortingChange={onTableSortingChange}
              loading={isLoading || isFetching}
              noDataTitle="No data"
              noDataMessage="No rows for current filters"
              showPagination={false}
              height={250}
            />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

export default SignalPreviewTable;
